import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';

import { EnumNgEventName } from '../../shared/event-animation.data';
import { NgContinuousAnimationService } from '../../shared/services/continuous-animation.service';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function pulseShrink(scale: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	scale = scale ? scale : 0.9;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility	: AUTO_STYLE,
				transform	: 'scale3d(1, 1, 1)',
				easing		: 'ease',
				offset		: 0
			}),
			style({
				transform	: `scale3d(${scale}, ${scale}, ${scale})`,
				easing		: 'ease',
				offset		: 0.5
			}),
			style({
				transform	: 'scale3d(1, 1, 1)',
				easing		: 'ease',
				offset		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * PulseShrink on Event
 */
@Directive({
    selector: '[ngPulseShrinkOnEvent]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgPulseShrinkOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement 					= null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale value is relative to the animation functionality
		if (this.effectOptions?.scale && this.effectOptions?.scale > 1) throw new Error(`PulseShrinkOnEventDirective => ngOnInit => FATAL ERROR: Provided scale value should be lower than 1: ${this.effectOptions?.scale}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: pulseShrink(this.effectOptions?.scale),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * PulseShrink on Mouse Hover
 */
@Directive({
    selector: '[ngPulseShrinkOnMouseHover]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgPulseShrinkOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement 					= null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale value is relative to the animation functionality
		if (this.timingOptions?.scale && this.timingOptions?.scale > 1) throw new Error(`PulseShrinkOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided scale value should be lower than 1: ${this.timingOptions?.scale}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: pulseShrink(this.timingOptions?.scale),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	scale : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	scale : number;
}
